import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import { CommunicationType, EmailProvider } from "@/enums/communicationLogs";
import { NotableType, NoteType } from "@/enums/dealActivity";
import { convertToArray, getEnumKeyFromValue } from "@/helpers/common";
import { STATUS_COLOR_CODES } from "@/helpers/constants";
import { WORKFLOW_TO_APP_STATUS } from "@/helpers/constants/workflow";
import { useAuth } from "@/hooks/auth";
import { useActiveWorkflowTemplate } from "@/hooks/workflow";
import { ROUTE_INBOX } from "@/router/routes";

import IconEmail from "@/components/icons/IconEmail.vue";
import IconGoogleColored from "@/components/icons/IconGoogleColored.vue";

import type { ApplicationStatus } from "@/models/applications";
import type { NoteLogData } from "@/models/communicationLogs";

export const useCommunicationLogs = () => {
  const route = useRoute();
  const router = useRouter();

  const isInboxPage = computed(() => route.name === ROUTE_INBOX);

  const currentTab = computed(() => route.query.type?.toString() ?? "");

  const isCurrentTabEmail = computed(
    () => currentTab.value === CommunicationType.email
  );

  const isCurrentTabSms = computed(
    () => currentTab.value === CommunicationType.sms
  );

  const isCurrentTabCall = computed(
    () => currentTab.value === CommunicationType.call
  );

  const isCurrentTabNote = computed(
    () => currentTab.value === CommunicationType.note
  );

  const isCurrentTabTask = computed(
    () => currentTab.value === CommunicationType.task
  );

  const handleDropdownValueUpdate = async (
    value: unknown[],
    queryKey: string,
    isMultiple?: boolean,
    selected?: string[]
  ) => {
    if (selected?.length && !value.length) {
      await handleFiltering(value as string[], queryKey, isMultiple);
    }
    if (!value.length) {
      return [];
    }
    const inputValue = value as string[];
    return isMultiple ? inputValue : [inputValue[inputValue.length - 1]];
  };

  const handleFiltering = async (
    value: string[],
    queryKey: string,
    isMultiple?: boolean
  ) => {
    await router.push({
      query: {
        ...route.query,
        [queryKey]: isMultiple ? value : value[0],
      },
    });
  };

  const getArrayQueryValue = (queryKey: string) => {
    const value = route.query[queryKey];
    return (Array.isArray(value) ? value : convertToArray(value)) as string[];
  };

  return {
    isInboxPage,
    currentTab,
    isCurrentTabEmail,
    isCurrentTabSms,
    isCurrentTabCall,
    isCurrentTabNote,
    isCurrentTabTask,
    handleDropdownValueUpdate,
    handleFiltering,
    getArrayQueryValue,
  };
};

export const useNoteLogData = (data: NoteLogData) => {
  const { getters } = useStore();
  const { t } = useI18n();

  const callDispositions = computed<Record<string, string>>(
    () => getters["options/callDispositions"]
  );

  const statusDescription = computed(() => {
    if (
      data.notable_type ===
      getEnumKeyFromValue(NotableType, NoteType.Application)
    ) {
      return data.stage_name;
    }
    if (
      data.notable_type === getEnumKeyFromValue(NotableType, NoteType.Business)
    ) {
      return t("DEALS.NOTES.BUSINESS_NOTE");
    }
    if (
      data.notable_type === getEnumKeyFromValue(NotableType, NoteType.Call) &&
      data.call_disposition
    ) {
      return callDispositions.value[data.call_disposition] || "";
    }
    return "";
  });

  const colorCode = computed(
    () =>
      STATUS_COLOR_CODES[
        WORKFLOW_TO_APP_STATUS[data.stage_type] as ApplicationStatus
      ]
  );

  return {
    statusDescription,
    colorCode,
  };
};

export const useEmailLogInfo = (
  data:
    | {
        provider: EmailProvider;
        is_outgoing: boolean | null;
      }
    | undefined
) => {
  const isGmail = computed(() => data?.provider === EmailProvider.Gmail);
  const iconInfo = computed(() => ({
    icon: isGmail.value ? IconGoogleColored : IconEmail,
    backgroundClass: isGmail.value ? "border" : "bg-secondary-blue-4",
    isOutgoing: data?.is_outgoing ?? undefined,
    viewBox: isGmail.value ? "0 0 14 14" : "0 0 16 16",
    height: isGmail.value ? 14 : 12,
    width: isGmail.value ? 14 : 12,
  }));

  return {
    isGmail,
    iconInfo,
  };
};

export const useActivityPermissions = () => {
  const { activeTemplate } = useActiveWorkflowTemplate();
  const { isLendflowUser } = useAuth();

  const canCreateTasks = computed(() => {
    if (isLendflowUser) {
      return true;
    }

    return (
      activeTemplate.value.content?.activity_hub?.active_tabs?.includes(
        CommunicationType.task
      ) ?? false
    );
  });

  return {
    canCreateTasks,
  };
};
